import {
    useNavigate,
    useParams,
} from 'react-router-dom';
import {
    Alert,
    Grid,
} from '@mantine/core';
import {I18n} from 'aws-amplify/utils';

import {
    FlexiCloseButton,
    FormPageLayout,
    PlatformLoader,
} from '@flexinet/ui-components';

import {Profile} from '../components/Profile';
import {DetailPageNavigation} from '../navigation/DetailPageNavigation';
import {useHostedZone} from '../hooks/useHostedZone';
import config from '../config';

// FNP-973: READ-ONLY detail view. No edit mode and no delete — the deployed
// schema has no hosted-zone mutation (writes are FNP-977).
export const DetailPage = () => {

    const {id} = useParams();
    const navigate = useNavigate();

    if (typeof id === 'undefined') {
        throw new Error('id is required parameter');
    }

    // The id arrives percent-encoded from the table link. Route 53 zone ids are
    // alphanumeric today, but decoding is what makes the link and the fetch
    // agree rather than relying on that.
    const zoneId = decodeURIComponent(id);

    const {
        record,
        loading,
        isError,
        notFound,
    } = useHostedZone(zoneId);

    if (loading) {
        return <PlatformLoader message={I18n.get('Loading hosted zone...Please wait')}/>;
    }

    // The two failures read differently to an operator: one is "the API broke",
    // the other is "that zone is not here".
    if (isError) {
        return (
            <Alert color='red' title={I18n.get('Could not load hosted zone')}>
                {I18n.get('The domains API did not return this hosted zone.')} <code>{zoneId}</code>
            </Alert>
        );
    }

    if (notFound || !record) {
        return (
            <Alert color='yellow' title={I18n.get('Hosted zone not found')}>
                {I18n.get('No hosted zone found for id')} <code>{zoneId}</code>
            </Alert>
        );
    }

    return (
        <FormPageLayout
            title={record.name}
            pageNavigation={<DetailPageNavigation component={record}/>}
            tools={[
                <FlexiCloseButton
                    key={'close'}
                    onClick={() => navigate(config.basePath)}
                >
                    {I18n.get('Close')}
                </FlexiCloseButton>,
            ]}
        >
            <Grid>
                <Grid.Col span={{
                    base: 12,
                    lg: 4,
                }}>
                    {/* The delegation set (nameServers) lives here — it is the
                        field this page exists to show. */}
                    <Profile record={record}/>
                </Grid.Col>
                <Grid.Col span={{
                    base: 12,
                    lg: 8,
                }}>
                    {/*
                      ─── RESERVED FOR FNP-1085: Record Sets ───

                      The zone's resource record sets nest here, in the wide
                      column, mirroring how synthetic-users hangs its
                      ObservationsPanel off the detail view.

                      Deliberately EMPTY in FNP-973. `listResourceRecordSets`
                      has no IAM permission in any environment today and returns
                      `Lambda:Unhandled`; re-granting it is owner-gated
                      (FNP-1084). Rendering a placeholder panel here would
                      promise a feature the API cannot serve, so the column is
                      left for FNP-1085 to fill rather than stubbed.
                    */}
                </Grid.Col>
            </Grid>
        </FormPageLayout>
    );
};
